<script setup lang="ts">
/**
 * HomeTypeSwitcher — pilule 3 positions (Tout / Chambre d'hôtes / Gîte) du hero
 * de la page d'accueil (spec-refonte-home-voyageur-switcher-type).
 *
 * Point d'entrée principal du filtre de type — pas un filtre secondaire. Pilote,
 * via le query param `?type`, tout le re-render de la home (useHomeType).
 *
 * Accessibilité : radiogroup (rôles radio + navigation clavier native via
 * <button>), et la couleur ne va JAMAIS sans le mot (chaque option porte son
 * libellé écrit). Les 3 options (Tout / Chambre d'hôtes / Gîte) sont TOUJOURS
 * proposées, indépendamment du stock de gîtes — alignement sur la barre de
 * recherche /search (décision produit).
 *
 * Doctrine couleur (tokens sémantiques de type, spec 4.7.0) :
 *   cdh → sauge (--color-type-cdh) | gite → ocre (--color-type-gite) | all → encre
 */
import type { HomeType } from '~/composables/useHomeType'

const { type, setType } = useHomeType()

const { t } = useI18n()

interface Option {
  value: HomeType
  label: string
  activeVar: string | null // couleur de fond quand actif (null = neutre encre)
  activeText: string
  /** Teinte claire du fond au survol — préfigure la couleur d'état de l'option. */
  hoverVar: string
}

// Version claire de chaque couleur de type (mix avec du blanc) pour le hover.
const CDH_HOVER = 'color-mix(in oklab, var(--color-type-cdh) 14%, white)'
const GITE_HOVER = 'color-mix(in oklab, var(--color-type-gite) 16%, white)'
const ALL_HOVER = 'color-mix(in oklab, var(--color-paper-900) 8%, white)'

const options = computed<Option[]>(() => [
  {
    value: 'all',
    label: t('pages.home.homeType.labels.all'),
    activeVar: null,
    activeText: 'text-paper-50',
    hoverVar: ALL_HOVER,
  },
  {
    value: 'cdh',
    label: t('pages.home.homeType.labels.cdh'),
    activeVar: 'var(--color-type-cdh)',
    activeText: 'text-paper-50',
    hoverVar: CDH_HOVER,
  },
  {
    value: 'gite',
    label: t('pages.home.homeType.labels.gite'),
    activeVar: 'var(--color-type-gite)',
    activeText: 'text-paper-900', // encre quasi-noire : contraste AA sur ocre plein
    hoverVar: GITE_HOVER,
  },
])

// Navigation clavier WAI-ARIA radiogroup : tabindex roving (seul le radio coché
// est tabbable) + flèches gauche/droite pour changer de sélection.
const btnRefs = ref<HTMLButtonElement[]>([])

function focusIndex(i: number) {
  const list = options.value
  const idx = (i + list.length) % list.length
  const opt = list[idx]
  if (!opt) return
  setType(opt.value)
  nextTick(() => btnRefs.value[idx]?.focus())
}

function onKeydown(e: KeyboardEvent, current: number) {
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault()
    focusIndex(current + 1)
  }
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault()
    focusIndex(current - 1)
  }
}
</script>

<template>
  <div
    role="radiogroup"
    :aria-label="$t('pages.home.homeType.switcherAriaLabel')"
    class="type-switcher flex w-full sm:inline-flex sm:w-fit max-w-full items-center gap-1 rounded-lg border hairline bg-paper-50 p-1 align-middle"
  >
    <button
      v-for="(opt, i) in options"
      :key="opt.value"
      ref="btnRefs"
      type="button"
      role="radio"
      :aria-checked="type === opt.value"
      :tabindex="type === opt.value ? 0 : -1"
      class="type-switch-btn flex-1 sm:flex-none cursor-pointer whitespace-nowrap rounded-md px-2 sm:px-5 py-2 font-sans text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
      :class="[
        type === opt.value
          ? [opt.activeText, opt.activeVar ? '' : 'bg-paper-900']
          : 'text-paper-900/55 hover:text-paper-900',
      ]"
      :style="type === opt.value
        ? (opt.activeVar ? { backgroundColor: opt.activeVar } : undefined)
        : { '--hover-bg': opt.hoverVar }"
      @click="setType(opt.value)"
      @keydown="onKeydown($event, i)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<style scoped>
/* Hover des options inactives : teinte claire propre à la couleur d'état de
   l'option (sauge/ocre/gris), pilotée par --hover-bg. */
.type-switch-btn[aria-checked='false']:hover {
  background-color: var(--hover-bg);
}

@media (prefers-reduced-motion: reduce) {
  .type-switch-btn {
    transition: none;
  }
}
</style>
